import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, LogOut, Mail, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState("");
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadMessages = async (tk) => {
    const res = await axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${tk}` } });
    setMessages(res.data);
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const tk = res.data.access_token;
      setToken(tk);
      setPassword("");
      await loadMessages(tk);
    } catch {
      setError("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await loadMessages(token);
    } catch {
      setToken(null);
      setError("Session expirée, reconnectez-vous.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setMessages([]);
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#E5E5E5] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center gap-3 mb-8">
            <Lock className="w-5 h-5 text-[#75FB4E]" />
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#808080]">Espace privé</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl uppercase leading-none mb-10">Messages</h1>
          <form data-testid="admin-login-form" onSubmit={login} className="flex flex-col gap-6">
            <div>
              <label className="font-mono-tech text-[10px] tracking-[0.3em] text-[#808080] uppercase">Mot de passe</label>
              <input
                data-testid="admin-password-input"
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#75FB4E] outline-none py-4 text-base text-[#E5E5E5] placeholder:text-[#4a4a4a] transition-colors duration-300"
              />
            </div>
            {error && <p data-testid="admin-login-error" className="font-mono-tech text-xs text-[#75FB4E]">{error}</p>}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="group relative self-start overflow-hidden border border-[#2A2A2A] px-10 py-4 font-mono-tech text-xs tracking-[0.3em] uppercase text-[#E5E5E5] hover:border-[#75FB4E] transition-colors duration-300 disabled:opacity-50"
            >
              <span className="absolute inset-0 bg-[#75FB4E] translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out" />
              <span className="relative group-hover:text-[#050505] transition-colors duration-300">
                {loading ? "Connexion…" : "Se connecter"}
              </span>
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#E5E5E5] px-6 md:px-12 py-12">
      <div className="flex items-center justify-between mb-12">
        <div>
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#808080]">Espace privé</span>
          <h1 className="font-display text-4xl md:text-6xl uppercase leading-none mt-2">
            Messages <span className="text-[#75FB4E]">({messages.length})</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-refresh-button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-[#2A2A2A] px-4 py-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5E5E5] hover:border-[#75FB4E] hover:text-[#75FB4E] transition-colors duration-300"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 border border-[#2A2A2A] px-4 py-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#808080] hover:border-[#75FB4E] hover:text-[#75FB4E] transition-colors duration-300"
          >
            <LogOut className="w-4 h-4" /> Quitter
          </button>
        </div>
      </div>

      {messages.length === 0 ? (
        <p data-testid="admin-empty" className="font-mono-tech text-sm text-[#808080]">Aucun message pour le moment.</p>
      ) : (
        <div data-testid="admin-messages" className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {messages.map((m, i) => (
            <motion.div
              key={m.id || i}
              data-testid={`admin-message-${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.03 }}
              className="border border-[#1A1A1A] p-6 hover:border-[#75FB4E]/40 transition-colors duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="font-display text-2xl uppercase">{m.name}</span>
                <span className="font-mono-tech text-[10px] tracking-widest text-[#808080]">{fmtDate(m.timestamp)}</span>
              </div>
              <a
                href={`mailto:${m.email}`}
                className="inline-flex items-center gap-2 font-mono-tech text-xs text-[#75FB4E] hover:underline mb-4"
              >
                <Mail className="w-3.5 h-3.5" /> {m.email}
              </a>
              <p className="text-sm text-[#E5E5E5] leading-relaxed whitespace-pre-wrap">{m.message}</p>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
